/**
 * iter213 — Vehicle History Timeline panel.
 *
 * Reusable slide-in that renders a reverse-chronological aggregated
 * timeline for a UK registration. Sources: Yestimate + Garage Hive +
 * DVSA (latest 2 MOTs).
 *
 * Usage:
 *   <VehicleHistoryPanel
 *      reg="AB12CDE"
 *      isOpen={open}
 *      onClose={() => setOpen(false)}
 *   />
 *
 * Or as a full-page mount via `mode="page"` from a route component.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  X, Loader2, AlertCircle, Wrench, ShieldCheck, ShieldAlert,
  Car, User, Calendar, Gauge, ExternalLink, ChevronDown, ChevronUp,
  RefreshCw, History,
} from "lucide-react";
import { Button } from "../ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// --- helpers --------------------------------------------------------

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

function fmtMileage(n, unit) {
  if (n === null || n === undefined || n === "") return null;
  const num = typeof n === "number" ? n : parseInt(String(n).replace(/,/g, ""), 10);
  if (Number.isNaN(num)) return null;
  return `${num.toLocaleString()} ${unit || "mi"}`;
}

// --- source + type badges ------------------------------------------

const SOURCE_STYLES = {
  yestimate: "bg-teal-50 text-teal-800 border-teal-200",
  gh:        "bg-indigo-50 text-indigo-800 border-indigo-200",
  dvsa:      "bg-amber-50 text-amber-800 border-amber-200",
};

function SourceBadge({ source, label }) {
  const cls = SOURCE_STYLES[source] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-bold ${cls}`}
      data-testid={`vh-source-${source}`}
    >
      {label}
    </span>
  );
}

function TypeIcon({ type }) {
  if (type === "mot")   return <ShieldCheck className="w-4 h-4 text-teal-600" />;
  if (type === "diag")  return <Wrench className="w-4 h-4 text-slate-500" />;
  if (type === "gh_jobsheet") return <History className="w-4 h-4 text-indigo-600" />;
  return <Wrench className="w-4 h-4 text-slate-500" />;
}

function StatusPill({ status, label }) {
  if (!label) return null;
  const s = (status || "").toLowerCase();
  let cls = "bg-slate-100 text-slate-700 border-slate-200";
  if (s === "pass" || s === "passed")           cls = "bg-emerald-50 text-emerald-700 border-emerald-200";
  else if (s === "fail" || s === "failed")      cls = "bg-red-50 text-red-700 border-red-200";
  else if (s.includes("progress") || s.includes("await")) cls = "bg-amber-50 text-amber-800 border-amber-200";
  else if (s === "completed" || s === "closed") cls = "bg-slate-50 text-slate-500 border-slate-200";
  else if (s === "cancelled")                    cls = "bg-red-50 text-red-500 border-red-100";
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold ${cls}`}>
      {label}
    </span>
  );
}

// --- timeline row --------------------------------------------------

function TimelineRow({ item, index }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetails =
    item.type === "mot" && (item.details?.defects?.length || 0) > 0 ||
    (item.details?.customer_name) ||
    (item.details?.services?.length || 0) > 0 ||
    (item.details?.expiry_date) ||
    (item.details?.amount_including_vat != null);

  const mileageStr = fmtMileage(item.mileage, item.mileage_unit);

  return (
    <li
      className="relative pl-8 pb-6 last:pb-0"
      data-testid={`vh-item-${item.id}`}
    >
      {/* Timeline rail dot */}
      <div className="absolute left-0 top-1 flex flex-col items-center">
        <div className="w-6 h-6 rounded-full bg-white border-2 border-slate-300 flex items-center justify-center shadow-sm">
          <TypeIcon type={item.type} />
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white hover:border-teal-300 transition-colors">
        <button
          type="button"
          onClick={() => hasDetails && setExpanded(v => !v)}
          className={`w-full text-left p-3 ${hasDetails ? "cursor-pointer" : "cursor-default"}`}
          data-testid={`vh-item-toggle-${item.id}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-mono text-slate-500">{fmtDate(item.date)}</span>
                <SourceBadge source={item.source} label={item.source_label} />
                <span className="text-sm font-semibold text-slate-900">{item.type_label}</span>
                <StatusPill status={item.status} label={item.status_label} />
              </div>

              <div className="mt-1 text-xs text-slate-600 leading-snug">
                {item.summary}
              </div>

              <div className="mt-1 flex items-center gap-3 text-[11px] text-slate-500 flex-wrap">
                {item.reference && (
                  <span className="font-mono">Ref: {item.reference}</span>
                )}
                {mileageStr && (
                  <span className="inline-flex items-center gap-1">
                    <Gauge className="w-3 h-3" /> {mileageStr}
                  </span>
                )}
                {item.details?.customer_name && (
                  <span className="inline-flex items-center gap-1">
                    <User className="w-3 h-3" /> {item.details.customer_name}
                  </span>
                )}
              </div>
            </div>

            {hasDetails && (
              <div className="shrink-0 pt-1 text-slate-400">
                {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            )}
          </div>

          {/* Expanded details */}
          {expanded && (
            <div className="mt-3 pt-3 border-t border-slate-100 space-y-2 text-xs text-slate-700"
                 data-testid={`vh-item-details-${item.id}`}>
              {item.type === "mot" && item.details?.defects?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">
                    Defects & advisories {item.details?.total_defects ? `(${item.details.total_defects} total)` : ""}
                  </div>
                  <ul className="space-y-1">
                    {item.details.defects.map((d, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <DefectSeverityDot type={d.type} dangerous={d.dangerous} />
                        <span className="leading-snug"><span className="uppercase text-[10px] font-bold text-slate-500 mr-1">{d.type}</span>{d.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {item.type === "mot" && item.details?.expiry_date && (
                <div className="flex items-center gap-1 text-[11px] text-slate-500">
                  <Calendar className="w-3 h-3" /> Expiry: {fmtDate(item.details.expiry_date)}
                </div>
              )}

              {item.details?.services?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">
                    Services requested
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {item.details.services.map((s, i) => (
                      <span key={i} className="inline-block rounded bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {item.details?.amount_including_vat != null && (
                <div className="text-[11px]">Amount (inc. VAT): £{Number(item.details.amount_including_vat).toFixed(2)}</div>
              )}

              {item.details?.requested_by && (
                <div className="text-[11px] text-slate-500">Requested by: {item.details.requested_by}</div>
              )}
            </div>
          )}
        </button>
      </div>
    </li>
  );
}

function DefectSeverityDot({ type, dangerous }) {
  const t = (type || "").toUpperCase();
  let cls = "bg-slate-400";
  if (t === "DANGEROUS" || dangerous) cls = "bg-red-600";
  else if (t === "MAJOR") cls = "bg-red-500";
  else if (t === "MINOR") cls = "bg-amber-400";
  else if (t === "ADVISORY") cls = "bg-blue-400";
  else if (t === "PRS") cls = "bg-emerald-400";
  return <span className={`inline-block w-2 h-2 rounded-full mt-1 ${cls}`} />;
}


// --- main component -----------------------------------------------

export function VehicleHistoryPanel({
  reg,
  isOpen = true,
  onClose,
  mode = "overlay",   // "overlay" | "page"
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [data, setData]       = useState(null);

  const load = useCallback(async () => {
    if (!reg) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const url = `${API}/foh/vehicles/${encodeURIComponent(reg)}/history`;
      const r = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load vehicle history.");
    } finally {
      setLoading(false);
    }
  }, [reg]);

  useEffect(() => {
    if (isOpen && reg) load();
  }, [isOpen, reg, load]);

  const body = (
    <div className={mode === "overlay"
      ? "bg-white w-full max-w-2xl h-full flex flex-col shadow-2xl ml-auto"
      : "bg-white w-full min-h-screen flex flex-col"
    } data-testid="vehicle-history-panel">
      {/* Header */}
      <div className="border-b border-slate-200 px-5 py-4 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <History className="w-4 h-4 text-slate-500" />
            <h2 className="text-lg font-bold text-slate-900" data-testid="vh-title">Vehicle history</h2>
            {data?.reg_display && (
              <span className="font-mono font-bold px-2 py-0.5 bg-slate-900 text-white rounded text-sm" data-testid="vh-reg">
                {data.reg_display}
              </span>
            )}
          </div>
          {data?.vehicle_snapshot && (data.vehicle_snapshot.make || data.vehicle_snapshot.model) && (
            <div className="mt-1 flex items-center gap-3 text-xs text-slate-600 flex-wrap" data-testid="vh-vehicle-snapshot">
              <span className="inline-flex items-center gap-1">
                <Car className="w-3 h-3 text-slate-400" />
                {[data.vehicle_snapshot.make, data.vehicle_snapshot.model].filter(Boolean).join(" ")}
              </span>
              {data.vehicle_snapshot.fuel_type && <span>· {data.vehicle_snapshot.fuel_type}</span>}
              {data.vehicle_snapshot.colour && <span>· {data.vehicle_snapshot.colour}</span>}
              {data.vehicle_snapshot.mot_expiry && (
                <span>· MOT expires {fmtDate(data.vehicle_snapshot.mot_expiry)}</span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="p-1.5 rounded hover:bg-slate-100 text-slate-500 disabled:opacity-50"
            title="Refresh"
            data-testid="vh-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          {mode === "overlay" && (
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded hover:bg-slate-100 text-slate-500"
              title="Close"
              data-testid="vh-close"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Source badges bar */}
      {data?.sources_status && (
        <div className="px-5 py-2 border-b border-slate-100 bg-slate-50 flex items-center gap-2 flex-wrap text-[11px]"
             data-testid="vh-sources-bar">
          {["yestimate", "gh", "dvsa"].map(src => {
            const s = data.sources_status[src];
            if (!s) return null;
            const label = {yestimate:"Yestimate", gh:"Garage Hive", dvsa:"DVSA"}[src];
            const ok = s.ok;
            return (
              <span key={src}
                    className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 border ${ok ? "bg-white border-slate-200 text-slate-600" : "bg-red-50 border-red-200 text-red-700"}`}
                    data-testid={`vh-source-status-${src}`}
                    title={s.error || ""}>
                <SourceBadge source={src} label={label} />
                {ok
                  ? <span>{s.count}{s.note ? ` · ${s.note}` : ""}</span>
                  : <span className="inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" /> unavailable</span>
                }
              </span>
            );
          })}
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {loading && !data && (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-8" data-testid="vh-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading vehicle history…
          </div>
        )}

        {error && (
          <div className="p-4 rounded border border-red-200 bg-red-50 text-sm text-red-700 flex items-start gap-2"
               data-testid="vh-error">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>{error}</div>
          </div>
        )}

        {data && !loading && data.items.length === 0 && !error && (
          <div className="py-8 text-center text-sm text-slate-500" data-testid="vh-empty">
            <p className="font-semibold text-slate-800 mb-1">No history found for this vehicle.</p>
            <p className="text-slate-500">There are no Yestimate jobs, Garage Hive jobsheets or DVSA MOT records for this reg yet.</p>
          </div>
        )}

        {data && data.items.length > 0 && (
          <ol className="relative" data-testid="vh-timeline">
            {/* Vertical rail */}
            <div className="absolute left-3 top-2 bottom-2 w-px bg-slate-200" aria-hidden />
            {data.items.map((item, i) => (
              <TimelineRow key={item.id} item={item} index={i} />
            ))}
          </ol>
        )}
      </div>

      {/* Footer */}
      {data?.diagnostics && (
        <div className="px-5 py-2 border-t border-slate-100 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-400 flex items-center justify-between"
             data-testid="vh-diag">
          <span>{data.items.length} item{data.items.length === 1 ? "" : "s"}</span>
          <span>{data.diagnostics.total_ms}ms</span>
        </div>
      )}
    </div>
  );

  if (mode === "page") return body;

  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex"
      onClick={onClose}
      data-testid="vehicle-history-overlay"
    >
      <div className="ml-auto h-full w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
        {body}
      </div>
    </div>
  );
}

export default VehicleHistoryPanel;
